import { useState } from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { Calendar } from './ui/calendar'
import { cn } from '../lib/utils'
import { formatDate } from '../lib/translations'
import { strToDate, dateToStr } from './DatePicker'

type DateRangePickerProps = {
  from?: string
  to?: string
  onChange: (range: { from: string; to: string }) => void
  min?: string
  placeholder?: string
  language?: string
  className?: string
  'data-testid'?: string
}

const DateRangePicker = ({
  from,
  to,
  onChange,
  min,
  placeholder,
  language = 'en',
  className = '',
  'data-testid': testId,
}: DateRangePickerProps) => {
  const [open, setOpen] = useState(false)
  const fromDate = strToDate(from)
  const toDate = strToDate(to)
  const selected: any = fromDate ? { from: fromDate, to: toDate } : undefined

  // Year range backing the month/year dropdowns.
  const minDate = strToDate(min)
  const currentYear = new Date().getFullYear()
  const startYear = minDate ? minDate.getFullYear() : currentYear - 100
  const endYear = currentYear + 10

  const label = () => {
    if (!fromDate) {
      return placeholder || (language === 'ka' ? 'აირჩიეთ თარიღები' : 'Pick dates')
    }
    if (!toDate || dateToStr(fromDate) === dateToStr(toDate)) {
      return formatDate(from, language)
    }
    return `${formatDate(from, language)} – ${formatDate(to, language)}`
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            'flex h-12 w-full items-center gap-3 rounded-2xl border border-[#1b1033]/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 text-left text-[15px] transition-colors hover:border-[#ff4d8d]/40 dark:hover:border-white/20 focus-visible:outline-none focus-visible:border-[#ff4d8d] focus-visible:ring-2 focus-visible:ring-[#ff4d8d]/25 motion-reduce:transition-none',
            className,
          )}
        >
          <CalendarIcon className="w-5 h-5 text-[#8b5cf6]/70 shrink-0" />
          <span className={fromDate ? 'text-[#1b1033] dark:text-[#f4f0ff]' : 'text-[#4a3d66]/50 dark:text-[#c3b8dd]/60'}>
            {label()}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          captionLayout="dropdown-buttons"
          fromYear={startYear}
          toYear={endYear}
          selected={selected}
          defaultMonth={fromDate}
          onSelect={(range: any) =>
            onChange({ from: dateToStr(range?.from), to: dateToStr(range?.to) })
          }
          disabled={min ? { before: strToDate(min) } : undefined}
          numberOfMonths={1}
          initialFocus
        />
        <div className="flex justify-end border-t border-[#1b1033]/10 dark:border-white/10 p-2">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-full px-3 py-1.5 text-sm font-semibold text-[#ff4d8d] transition-colors hover:bg-[#ff4d8d]/10 motion-reduce:transition-none"
            data-testid={testId ? `${testId}-done` : undefined}
          >
            {language === 'ka' ? 'მზადაა' : 'Done'}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default DateRangePicker
